import { useState } from "react";
import { Facebook, X, Loader2, ExternalLink, CheckCircle2 } from "lucide-react";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const FB_BLUE = "#1877F2";

export function ShareToFacebook({ defaultCaption = "", image = "", images = null, link = "", source = "", testid = "share-fb", compact = true, label = null }) {
  const [open, setOpen] = useState(false);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null); // null=loading; {configured, page_name, valid?}
  const [result, setResult] = useState(null);

  // Resolve the photo set: explicit `images` array wins, else the single `image`.
  const photoList = (images && images.length ? images : (image ? [image] : [])).filter(Boolean);
  const multi = photoList.length > 1;

  const openModal = async () => {
    setCaption(defaultCaption || "");
    setResult(null);
    setStatus(null);
    setOpen(true);
    try {
      const { data } = await api.get("/admin/facebook/status");
      setStatus(data);
    } catch {
      setStatus({ configured: false });
    }
  };

  const post = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/facebook/share", { caption, image: photoList[0] || "", images: photoList, link, source });
      setResult(data);
      toast.success(multi ? `Posted ${photoList.length} photos to your Facebook Page!` : "Posted to your Facebook Page!");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Facebook post failed");
    } finally {
      setBusy(false);
    }
  };

  const close = () => { if (!busy) setOpen(false); };
  const canPost = status?.configured && status?.valid !== false && (photoList.length > 0 || caption.trim());
  const btnLabel = label || (compact ? "Facebook" : "Share to Facebook");

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        data-testid={`${testid}-btn`}
        className="inline-flex items-center gap-1.5 text-sm border rounded-md px-3 py-2 font-semibold transition-colors hover:bg-[#1877F2]/10"
        style={{ borderColor: FB_BLUE, color: FB_BLUE }}
      >
        <Facebook className="h-4 w-4" /> {btnLabel}
      </button>

      {open && (
        <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4" onClick={close} data-testid={`${testid}-modal`}>
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E7EB]">
              <h3 className="font-heading font-bold text-lg inline-flex items-center gap-2">
                <Facebook className="h-5 w-5" style={{ color: FB_BLUE }} /> Share to Facebook
              </h3>
              <button onClick={close} data-testid={`${testid}-close`} className="text-[#999999] hover:text-[#111111]"><X className="h-5 w-5" /></button>
            </div>

            <div className="p-5 space-y-4">
              {status === null && (
                <p className="text-sm text-[#555555] inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Checking connection…</p>
              )}
              {status && !status.configured && (
                <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800" data-testid={`${testid}-not-connected`}>
                  Facebook isn&apos;t connected yet. Open <strong>Site Content → Facebook Connection</strong>, paste your Page ID and access token, then come back.
                </div>
              )}
              {status && status.configured && status.valid === false && (
                <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">Connection problem: {status.error || "token invalid"}. Re-connect in Site Content → Facebook Connection.</div>
              )}
              {status && status.configured && status.valid !== false && status.page_name && (
                <p className="text-xs text-[#555555]">Posting to <strong>{status.page_name}</strong></p>
              )}

              {result ? (
                <div className="text-center py-4 space-y-3" data-testid={`${testid}-success`}>
                  <CheckCircle2 className="h-10 w-10 text-green-600 mx-auto" />
                  <p className="font-semibold">Posted to your Facebook Page!</p>
                  {result.url && (
                    <a href={result.url} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-view`} className="inline-flex items-center gap-1.5 font-semibold text-sm" style={{ color: FB_BLUE }}>
                      View post <ExternalLink className="h-4 w-4" />
                    </a>
                  )}
                  <div><button onClick={() => setOpen(false)} className="mt-2 text-sm text-[#555555] underline">Close</button></div>
                </div>
              ) : (
                <>
                  {photoList.length === 1 && (
                    <img src={fileUrl(photoList[0])} alt="To share" className="w-full max-h-64 object-contain rounded-md border border-[#E5E7EB] bg-[#0B0B0C]" />
                  )}
                  {multi && (
                    <div data-testid={`${testid}-gallery-preview`}>
                      <p className="text-xs text-[#555555] mb-1.5">Posting <strong>{photoList.length} photos</strong> as one album.</p>
                      <div className="grid grid-cols-4 gap-1.5">
                        {photoList.map((p, pi) => (
                          <img key={pi} src={fileUrl(p)} alt={`Photo ${pi + 1}`} className="aspect-square object-cover rounded-md border border-[#E5E7EB] bg-[#0B0B0C]" />
                        ))}
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-[#333333] mb-1">Caption {photoList.length ? "(posted with the photos)" : ""}</label>
                    <textarea
                      data-testid={`${testid}-caption`}
                      className="w-full rounded-md border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1877F2]"
                      rows={5}
                      value={caption}
                      onChange={(e) => setCaption(e.target.value)}
                      placeholder="Write something to go with this post…"
                    />
                    {!photoList.length && <p className="text-xs text-[#888888] mt-1">No image on this item — a text post will be created.</p>}
                  </div>
                  <button
                    onClick={post}
                    disabled={busy || !canPost}
                    data-testid={`${testid}-post`}
                    className="w-full inline-flex items-center justify-center gap-2 text-white rounded-md px-4 py-2.5 font-semibold disabled:opacity-50"
                    style={{ backgroundColor: FB_BLUE }}
                  >
                    {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Posting…</> : <><Facebook className="h-4 w-4" /> Post to Facebook</>}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
